import type { PublicCountryConfig } from './types.js';

/**
 * The shape of a Spanish NIF the web forms accept (the browser checks only the
 * SHAPE; `es.ts` does the checksum on the server):
 *
 * - DNI — 8 digits + control letter (`12345678Z`);
 * - NIE — X/Y/Z + 7 digits + control letter (`X1234567L`);
 * - K/L/M NIF — natural persons without a DNI/NIE: letter + 7 digits + letter;
 * - CIF — entity letter + 7 digits + control digit or letter (`B12345674`).
 *
 * Exported for `es.ts` and the tests, so the form and the server can never
 * disagree on what a complete id looks like.
 */
export const ES_NIF_SHAPE =
  '^(?:\\d{8}[A-Z]|[XYZ]\\d{7}[A-Z]|[KLM]\\d{7}[A-Z]|[ABCDEFGHJNPQRSUVW]\\d{7}[0-9A-J])$';

// MiLicita — Spain (the third deployment, after UZ and KZ). Spanish only at launch:
// `locales: ['es']`, `defaultLocale: 'es'`, so every path is bare (no /es prefix).
// Fixed decisions: /home/analyst/uzt-work/milicita-ops/AGENT-RULES.md §6.
export const esPublic: PublicCountryConfig = {
  code: 'es',
  locales: ['es'],
  defaultLocale: 'es',
  // The 17 comunidades autónomas + Ceuta + Melilla live in the DB `regions`
  // table with Spanish names, keyed by NUTS-2 code (`prisma/es-seed.ts`). The
  // feed and the site are both Spanish, so `localize` is a passthrough — the
  // same contract as KZ (ru in, ru out).
  regions: {
    localize(name, _locale) {
      return name && name.trim() ? name : null;
    },
  },
  currency: {
    code: 'EUR',
    symbol: '€',
    // Paired positionally with format.ts's [1e12, 1e9, 1e6, 1e3] ladder, the same
    // words `Intl.NumberFormat('es-ES', { notation: 'compact' })` uses: «mil» (10³),
    // «M» (10⁶, millones), «mil M» (10⁹, mil millones — Spanish has no "billion" at
    // 10⁹) and «B» (10¹², billones). Spelled out rather than read from Intl so the
    // output is identical in SSR, prerender and every browser.
    compactUnits: { es: ['B', 'mil M', 'M', 'mil'] },
    // The {{currency}} word next to an amount. `ru` is not a locale of this site:
    // it serves the API copy that is STILL Russian on ES (e-mails, the CRM topic,
    // bot lines — Spanish copy is wave 2), which reads `words['ru']` and would
    // otherwise print «сум» or nothing.
    words: { es: 'euros', ru: 'евро' },
    // Euros are stored WITH cents; budgets print «1.234,56» (es-ES symbols).
    numberLocale: 'es-ES',
  },
  brand: {
    // Always «MiLicita», capital L — «Milicita» reads as «mili cita».
    name: 'MiLicita',
    siteUrl: 'https://milicita.es',
    // Owner, 2026-10-20: the address is support@ (soporte@ is only a mailbox alias,
    // named nowhere in the UI — docs/how-to/milicita-email-setup.md).
    supportEmail: 'support@milicita.es',
    // No human Telegram contact is published on the Spanish site (the UZ/KZ
    // handle is the owner's personal account). Set it here if Spain gets one.
    supportTelegram: null,
    publishPhone: false,
  },
  // Spanish has no grammatical case: «de España», «en España» — the copy writes
  // the preposition, the token is the bare name in all three slots.
  // `ru`: for the still-Russian API copy, like `currency.words.ru` above.
  countryName: {
    es: { nom: 'España', gen: 'España', prep: 'España' },
    ru: { nom: 'Испания', gen: 'Испании', prep: 'Испании' },
  },
  // NIF/CIF/NIE: nine characters, letters and digits. The server checks the
  // control character (`es.ts`); the forms check only this shape.
  // `keepsTypedName`: a company the register does not know is still saved by
  // the name the reader typed (owner, 2026-10-21).
  companyIdForm: {
    label: 'NIF',
    digits: 9,
    alphanumeric: { pattern: ES_NIF_SHAPE },
    keepsTypedName: true,
  },
  phonePlaceholder: '+34 612 345 678',
  // Peninsular time, CET/CEST: PLACSP deadlines are Madrid wall clock, and the
  // day a limit resets on is the reader's. (Canarias run an hour behind; the
  // national feed and the site speak Madrid time.)
  timeZone: 'Europe/Madrid',
  // Meta Pixel of MiLicita (owner, 2026-10-20); no GA4 until the owner creates
  // it (''). In the EU both load only after cookie consent (`cookieConsent`
  // below swaps in the consent bootstrap — `index-html.consent.test.ts`).
  // `paywallOncePerLot` (owner 23.10): the wall counts a person on a lot, not a locked block.
  // `cookielessBeforeConsent` (owner 23.10): until the banner is answered, PostHog counts
  // page views and a few funnel events without storing anything on the device; the Pixel and
  // the normal PostHog (cookies, replay) stay behind the grant.
  analytics: {
    metaPixelId: '1739042453832204',
    ga4MeasurementId: '',
    paywallOncePerLot: true,
    cookielessBeforeConsent: true,
    // Every consented session is recorded while traffic is a few dozen visits a day (owner 23.10).
    replaySampleRate: 1,
  },
  // Owner 23.10: a Catalan/Galician/Basque title is shown in Castilian, the original underneath;
  // a long or opaque title is shown as a short one that says what is bought, likewise.
  content: { castilianTitles: true, readableTitles: true },
  // No bot until the owner creates one in BotFather; then `telegram: true` and
  // `telegramBot`. Alerts go by e-mail meanwhile, the first one included.
  messaging: {
    telegram: false,
    telegramBot: null,
    telegramChannel: null,
    emailFirstAlert: true,
    // Owner, 2026-10-21: no confirmation step — every alert email (digest,
    // results alert, the instant first lot) goes to an unconfirmed address too.
    emailWithoutConfirmation: true,
    // QA 22.10 Н1: one first lot per search, the one that fits it — the
    // create-time send reads the saved row, and a search gets one first lot.
    singleFirstAlert: true,
    // Owner, 2026-10-21: payment, abandoned checkout, failed renewal, cancel and
    // refund cards by e-mail (and a MiLicita bot later), never the Uzbek topic.
    ownerAlertsByEmail: true,
  },
  features: {
    // Off until Spanish award/price data is in (no CPV price history yet).
    priceIntel: false,
    teamPlans: true,
    // PLACSP publishes a buyer contact on every main-feed record; there is no
    // corporate scope in Spain at all, so the gate would gate nothing.
    corporateContactGate: false,
    // /start and /ip are Uzbek sales copy — they redirect to the catalogue here.
    conversionLandings: false,
    aiOnboarding: true,
    honestTeasers: true,
    // No supply-request source (`sourceKeys` has no `cooperation`).
    supplyScope: false,
    // PLACSP only: no World Bank & co. projects and no supply requests, so /ifi
    // and /supply redirect to the catalogue and have no row, chip or prerender.
    sections: [],
    // EU: no Pixel, PostHog, GA4 or first-party beacon before the visitor consents (LSSI-CE 22.2).
    cookieConsent: true,
    // Owner's call, 2026-10-20 (Igor: «источник и документы НЕ бесплатно, всё
    // делаем ± как на узтендер»), overriding brief §4: although PLACSP publishes
    // them, the pliegos, the buyer contact, the original link and the expediente
    // number stay Pro, as on UZ. `true` opens them to every viewer again.
    publicSourceData: false,
    // Same call: the lot's award (adjudicatario, NIF, importe) is walled behind
    // Pro on the lot, /results, the alerts and the prerender, as on UZ.
    // `false` shows it to everyone again.
    outcomeWall: true,
    // «Próximos vencimientos» (owner, 2026-10-20: «бургер-меню делай, это
    // полезно»): award date + DurationMeasure → the contract's end date.
    expiringContracts: true,
    // «Suministros · Obras · Servicios» tabs although `priceIntel` is off: every
    // CPV code fills `procurement_kind` (99 % of the live catalogue, QA 20.10).
    kindTabs: true,
    // The first screen repeats the ad («Crea tu alerta gratis»), and its button
    // opens «¿Qué ofreces?» without a category (review 21.10, «first hour» 1).
    alertLanding: true,
    // Pro is sold by what PLACSP does not give: who wins, for how much, against
    // whom (review 21.10 «first hour», item 3). Same Pro, same padlocks.
    marketFirstPitch: true,
    // One real figure on the lot's market card (median bidders) and the
    // «¿Por cuánto se gana?» line under the budget (review 21.10, item 4).
    freeBidderMedian: true,
    // Owner, 22.10: «¿Qué ofreces?» is the description only; the NIF has its own
    // step, which offers (never applies silently) the CPV codes of its awards.
    aiCompanyStep: true,
    // Negative QA 22.10 (A4): a guest's «Suscribirme a Pro» goes to /register and
    // back to /plans with the card form open; no onboarding sheet over /plans.
    purchaseFirst: true,
    // Negative QA 22.10 (C7, C10, C11): «Configurar mis alertas con IA» brings the
    // AI step back; «Cambiar» on the proposal returns to the description.
    aiOnboardingReentry: true,
    // Negative QA 22.10 (A2.7): `?page=999` says «No hay más resultados» and
    // links to page 1, instead of an empty list that blamed the filters.
    pastLastPage: true,
    // Negative QA 22.10 (B5): /login answers an empty or malformed field itself,
    // in Spanish, instead of the browser's «Please fill out this field.».
    loginOwnErrors: true,
    // CPV check 22.10 (defect 1): a saved search opens the catalogue with all of
    // its codes, words and regions (`alert=`), not the first of each ANDed.
    alertCriteriaLink: true,
    // CPV check 22.10 (defect 2): a search with CPV codes matches by its codes;
    // its words narrow only a search that has no codes.
    codesOverTerms: true,
    // CPV check 22.10 (defect 5): a code chip is named from the full code table,
    // not from the browse list of codes with 5+ lots.
    codeNamesFromTable: true,
    // CPV check 22.10 (defect 7): «~N al día» by the buyer's publication date —
    // the 19.10 bulk import is not a day's flow.
    ratePerDayByPublication: true,
    // Persona test 22.10 (D1): «+ Guardar tu búsqueda» keeps the guest's filters
    // through /register, and the onboarding sheet waits for the replay.
    guestSearchCarried: true,
    // Persona test 22.10 (D2): a padlock tap no longer hijacks «Crear alerta gratis»;
    // only a pressed pay control, within 30 minutes, sends a sign-in back to /plans.
    purchaseIntentScoped: true,
    // Persona test 22.10 (D5): a search with CPV codes matches by its codes — its
    // category (a label here) no longer widens it to the whole category.
    codesOverCategory: true,
    // Persona test 22.10 (D6): «Omitir» on the NIF step is kept on the account, so the
    // sheet does not come back on every reload and device.
    companySkipRemembered: true,
    // Persona test 22.10 (D7): a refused login/register code empties the boxes and
    // puts the caret in the first — the API's refusal is recognised by its message.
    refusedCodeCleared: true,
    // Persona test 22.10 (D8): /billing/return with no payment behind it says so
    // instead of «¡Gracias por tu pago!».
    neutralBillingReturn: true,
    // Persona test 22.10 (D14): the NIF step saves silently — no «ya vinculado a otra
    // cuenta» beside «NIF guardado» (that line is UZ/KZ's claim-your-company).
    companyLinkNoticeHidden: true,
    // Owner 23.10 (real searches «reforma» → 0, «Obras» → 1 inside the filters of an old alert link):
    // a word typed with narrowing filters on says so, and offers the whole catalogue.
    searchScopeLine: true,
  },
  // Stripe Checkout, monthly only, no invoice, no ИП tier — like Kazakhstan.
  // Cards are accepted by the owner's US company (no Spanish SL).
  payments: {
    checkout: 'stripe',
    yearlyPlans: false,
    bankInvoice: false,
    ipPlan: false,
    marks: ['visa', 'mastercard', 'stripe'],
    cardPayee: {
      name: 'AI CRM Inc',
      address: '251 Little Falls Drive, Wilmington, New Castle County, DE 19808, USA',
      phone: '302-636-5401',
      processor: 'Stripe',
    },
    // NO IVA (owner, 2026-10-20 — overrides brief §4's Stripe Tax + OSS): the
    // seller is AI CRM Inc, a US company not established in the EU and not
    // registered for IVA, and Stripe Tax is NOT activated on the account. Prices
    // are quoted and charged without IVA («49 € / mes · IVA no incluido»); a
    // Spanish company or autónomo self-assesses it (inversión del sujeto pasivo,
    // art. 84.Uno.2.º LIVA). No `stripeTax` here — the checkout must open on an
    // account without Stripe Tax.
    vatNotCharged: true,
    // The invoice still names the buyer: the Stripe page asks for the NIF / VAT
    // number with the legal name, and the full billing address. Neither needs
    // Stripe Tax.
    checkoutCollects: { taxId: true, billingAddress: true },
  },
};
